<template>
  <div class="location-social-posts-table">
    <div class="location-social-posts-table__heading">
      <h2 class="global-h4 location-social-posts-table__title">Posts</h2>

      <div v-if="locationChip" class="location-social-posts-table__chip">
        <LocationAvatar :src="locationChip.icon" :name="locationChip.name" :size="20" />
        <span class="location-social-posts-table__chip-name">{{ locationChip.name }}</span>
        <button type="button" class="location-social-posts-table__chip-clear" aria-label="Show all posts" @click="emit('clearLocationFilter')">
          <IconEmbedded name="remove_2-5" :size="13" color="#667085" />
        </button>
      </div>
    </div>

    <div class="location-social-posts-table__scroll">
      <table class="location-social-posts-table__table">
        <thead>
          <tr class="location-social-posts-table__header-row">
            <th class="location-social-posts-table__th location-social-posts-table__th--thumb"></th>
            <th class="location-social-posts-table__th location-social-posts-table__th--sort" :aria-sort="ariaSort('caption')" @click="sortByColumn('caption')">
              <span class="location-social-posts-table__th-inner location-social-posts-table__th-inner--start">
                Post
                <IconEmbedded v-if="sortKey === 'caption'" class="location-social-posts-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-posts-table__th location-social-posts-table__th--date location-social-posts-table__th--sort" :aria-sort="ariaSort('postedOn')" @click="sortByColumn('postedOn')">
              <span class="location-social-posts-table__th-inner location-social-posts-table__th-inner--start">
                Posted
                <IconEmbedded v-if="sortKey === 'postedOn'" class="location-social-posts-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-posts-table__th location-social-posts-table__th--networks">Networks</th>
            <th class="location-social-posts-table__th location-social-posts-table__th--number location-social-posts-table__th--sort" :aria-sort="ariaSort('reach')" @click="sortByColumn('reach')">
              <span class="location-social-posts-table__th-inner">
                Reach
                <IconEmbedded v-if="sortKey === 'reach'" class="location-social-posts-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-posts-table__th location-social-posts-table__th--number location-social-posts-table__th--sort" :aria-sort="ariaSort('engagements')" @click="sortByColumn('engagements')">
              <span class="location-social-posts-table__th-inner">
                Engagements
                <IconEmbedded v-if="sortKey === 'engagements'" class="location-social-posts-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-posts-table__th location-social-posts-table__th--number location-social-posts-table__th--sort" :aria-sort="ariaSort('engagementRatePercent')" @click="sortByColumn('engagementRatePercent')">
              <span class="location-social-posts-table__th-inner">
                Rate
                <IconEmbedded v-if="sortKey === 'engagementRatePercent'" class="location-social-posts-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!visiblePosts.length">
            <td class="location-social-posts-table__empty-cell" colspan="7">No posts match</td>
          </tr>
          <tr v-for="post in visiblePosts" :key="post.socialPostId" class="location-social-posts-table__row">
            <td class="location-social-posts-table__td location-social-posts-table__td--thumb">
              <img v-if="post.firstAssetId" class="location-social-posts-table__thumb" :src="thumbnailSrc(post.firstAssetId)" alt="" loading="lazy" />
              <div v-else class="location-social-posts-table__thumb location-social-posts-table__thumb--empty"></div>
            </td>
            <td class="location-social-posts-table__td">
              <div v-if="postTypeMeta(post)" class="location-social-posts-table__kind">
                <IconEmbedded class="location-social-posts-table__kind-icon" :name="postTypeMeta(post).icon" :size="14" />
                <span>{{ postTypeMeta(post).label }}</span>
              </div>
              <div v-if="captionText(post)" class="location-social-posts-table__caption">{{ captionText(post) }}</div>
              <div v-if="!post.hasAnalytics" class="location-social-posts-table__hint">No metrics yet</div>
            </td>
            <td class="location-social-posts-table__td location-social-posts-table__td--date">{{ postedText(post) }}</td>
            <td class="location-social-posts-table__td location-social-posts-table__td--networks">
              <NetworkIcons v-if="post.channels?.length" :channels="post.channels" :size="18" />
              <span v-else class="location-social-posts-table__muted">—</span>
            </td>
            <td class="location-social-posts-table__td location-social-posts-table__td--number">{{ post.hasAnalytics ? formatCount(post.reach) : "—" }}</td>
            <td class="location-social-posts-table__td location-social-posts-table__td--number">{{ post.hasAnalytics ? formatCount(post.engagements) : "—" }}</td>
            <td class="location-social-posts-table__td location-social-posts-table__td--number">{{ post.hasAnalytics ? formatRate(post.engagementRatePercent) : "—" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DayjsExtended from "@logic/DayjsExtended";
import FileUtils from "@logic/FileUtils";
import NumberFormatting from "@logic/NumberFormatting";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import NetworkIcons from "./NetworkIcons.vue";

// Types
import type { LocationSocialMetricsPostRow, LocationSocialMetricsRow } from "@contracts/socialReports";
import type { IconName } from "@contracts/IconName";

type SortKey = "caption" | "postedOn" | "reach" | "engagements" | "engagementRatePercent";
type SortDir = "asc" | "desc";
const METRIC_SORT_KEYS: SortKey[] = ["reach", "engagements", "engagementRatePercent"];

const props = withDefaults(
  defineProps<{
    posts: LocationSocialMetricsPostRow[];
    /** When set, only posts featuring this location are listed (shown as a removable chip). */
    locationId?: string | null;
    /** Used to name the location in the filter chip. */
    rows?: LocationSocialMetricsRow[];
  }>(),
  { locationId: null, rows: () => [] }
);

const emit = defineEmits<{
  (e: "clearLocationFilter"): void;
}>();

const locationChip = computed<LocationSocialMetricsRow | null>(() => {
  if (!props.locationId) return null;
  return props.rows.find(r => r.locationId === props.locationId) ?? null;
});

const sortKey = ref<SortKey | null>(null);
const sortDir = ref<SortDir>("desc");

function defaultDir(key: SortKey): SortDir {
  return key === "caption" || key === "postedOn" ? "asc" : "desc";
}

function sortByColumn(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
    return;
  }
  sortKey.value = key;
  sortDir.value = defaultDir(key);
}

function ariaSort(key: SortKey): "ascending" | "descending" | "none" {
  if (sortKey.value !== key) return "none";
  return sortDir.value === "asc" ? "ascending" : "descending";
}

const sortCaret = computed<IconName>(() => (sortDir.value === "asc" ? "caret-top_2-5" : "caret-bottom_2-5"));

function postedOnValue(post: LocationSocialMetricsPostRow): number {
  if (!post.postedOn) return Number.NEGATIVE_INFINITY;
  const date = DayjsExtended(post.postedOn);
  return date.isValid() ? date.valueOf() : Number.NEGATIVE_INFINITY;
}

/** Backend already sorts by postedOn desc until a header is clicked. */
const visiblePosts = computed<LocationSocialMetricsPostRow[]>(() => {
  const filtered = props.locationId ? props.posts.filter(p => (p.locationIds ?? []).includes(props.locationId as string)) : props.posts;
  if (!sortKey.value) return filtered;

  const key = sortKey.value;
  const dir = sortDir.value === "asc" ? 1 : -1;
  const isMetric = METRIC_SORT_KEYS.includes(key);
  return [...filtered].sort((a, b) => {
    if (isMetric && a.hasAnalytics !== b.hasAnalytics) return a.hasAnalytics ? -1 : 1;
    if (key === "caption") return dir * (a.captionExcerpt ?? "").localeCompare(b.captionExcerpt ?? "", undefined, { sensitivity: "base" });
    if (key === "postedOn") {
      const aHasDate = postedOnValue(a) !== Number.NEGATIVE_INFINITY;
      const bHasDate = postedOnValue(b) !== Number.NEGATIVE_INFINITY;
      if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
      return dir * (postedOnValue(a) - postedOnValue(b));
    }
    return dir * (a[key] - b[key]);
  });
});

function thumbnailSrc(assetId: string): string {
  return FileUtils.getThumbnailPath(assetId);
}

function postedText(post: LocationSocialMetricsPostRow): string {
  if (!post.postedOn) return "—";
  const date = DayjsExtended(post.postedOn);
  if (!date.isValid()) return "—";
  return date.format("MMM D, YYYY");
}

function postTypeMeta(post: LocationSocialMetricsPostRow): { label: string; icon: IconName } | null {
  if (post.postType === "Reel") return { label: "Reel", icon: "social-reel" };
  if (post.postType === "Story") return { label: "Story", icon: "social-story" };
  return null;
}

function captionText(post: LocationSocialMetricsPostRow): string {
  if (post.captionExcerpt) return post.captionExcerpt;
  if (post.postType === "Story" || post.postType === "Reel") return "";
  return "(no caption)";
}

function formatCount(value: number): string {
  return NumberFormatting.abbreviatedFormat(value) || "0";
}

function formatRate(value: number): string {
  return NumberFormatting.formatPercent(value) || "—";
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Location social posts table ================================================
.location-social-posts-table {
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__heading {
    margin-bottom: 16px;
    gap: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__chip {
    gap: 8px;
    padding: 4px 8px 4px 5px;
    border: 1px solid #e4e7ec;
    border-radius: 100px;
    display: inline-flex;
    align-items: center;
    background: #f4f6f8;
  }

  &__chip-name {
    max-width: 210px;
    color: #102f41;
    font: 500 13px/17px sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chip-clear {
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    border-radius: 100px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.06);

    &:hover {
      background: rgba(0, 0, 0, 0.12);
    }
  }

  &__scroll {
    max-width: 100%;
    overflow-x: hidden;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__header-row {
    border-bottom: 1px solid #f0f2f5;
  }

  &__th {
    padding: 0 10px 10px 0;
    color: #667085;
    font: 500 14px/18px sans-serif;
    text-align: left;
    white-space: nowrap;

    &--thumb {
      width: 56px;
    }

    &--date {
      width: 120px;
    }

    &--networks {
      width: 90px;
    }

    &--number {
      width: 110px;
      text-align: right;
    }

    &--sort {
      cursor: pointer;

      &:hover {
        color: #102f41;
      }
    }
  }

  &__th-inner {
    gap: 5px;
    display: inline-flex;
    justify-content: flex-end;
    align-items: center;

    &--start {
      justify-content: flex-start;
    }
  }

  &__caret {
    flex-shrink: 0;
  }

  &__row {
    border-bottom: 1px solid #f0f2f5;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__td {
    padding: 10px 10px 10px 0;
    color: #102f41;
    font: 400 14px/18px sans-serif;
    vertical-align: middle;

    &--thumb {
      width: 56px;
    }

    &--date {
      color: #667085;
      white-space: nowrap;
    }

    &--number {
      font-family: $font-family;
      font-weight: 600;
      text-align: right;
      white-space: nowrap;
    }
  }

  &__thumb {
    width: 44px;
    height: 44px;
    border-radius: 7px;
    display: block;
    object-fit: cover;
    background: #eef1f4;

    &--empty {
      background: #eef1f4;
    }
  }

  &__caption {
    max-width: 420px;
    overflow-wrap: anywhere;
  }

  &__kind {
    gap: 5px;
    margin-bottom: 2px;
    color: #667085;
    font: 500 12px/16px sans-serif;
    display: inline-flex;
    align-items: center;
  }

  &__kind-icon {
    flex-shrink: 0;

    :deep(svg) {
      fill: #667085;
    }
  }

  &__hint {
    margin-top: 3px;
    color: #667085;
    font: 400 12px/16px sans-serif;
  }

  &__muted {
    color: #667085;
  }

  &__empty-cell {
    padding: 18px 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-social-posts-table {
    padding: 18px 15px;

    &__scroll {
      overflow-x: auto;
    }

    &__table {
      min-width: 660px;
    }

    &__caption {
      max-width: 240px;
    }
  }
}
</style>
